import { createContext, ReactNode, useCallback, useState, useContext, SyntheticEvent } from 'react';
import { AlertColor } from '@mui/material';
import { emptyFunction } from 'utils/functions';
import {Snackbar} from 'components/feedbacks';

export const DEFAULT_AUTO_HIDE_DURATION = 3000;
export const DEFAULT_TRANSITION_DURATION = 500;

export type ShowSnackbarFunction = (message: ReactNode, autoHideDurationParam?: number | null, severity?: AlertColor) => void;

type SnackbarContextState = {
  autoHideDuration?: number | null;
  message?: ReactNode;
  severity?: AlertColor;
  open: boolean;
}

export type SnackbarContextType = SnackbarContextState & {
  transitionDuration?: number;
  onClose: (event: SyntheticEvent<Element, Event>) => void;
  errorSnackbar: ShowSnackbarFunction;
  successSnackbar: ShowSnackbarFunction;
  warningSnackbar: ShowSnackbarFunction;
  infoSnackbar: ShowSnackbarFunction;
};

const initialState = {
  autoHideDuration: DEFAULT_AUTO_HIDE_DURATION,
  transitionDuration: DEFAULT_TRANSITION_DURATION,
  message: undefined,
  severity: undefined,
  open: false,
  onClose: emptyFunction,
  errorSnackbar: emptyFunction,
  successSnackbar: emptyFunction,
  warningSnackbar: emptyFunction,
  infoSnackbar: emptyFunction,
};

export const SnackbarContext = createContext<SnackbarContextType>(initialState);
const { Provider } = SnackbarContext;

type SnackbarProviderProps = {
  children: ReactNode;
  transitionDuration?: number;
};

export const SnackbarProvider = ({ children, transitionDuration = DEFAULT_TRANSITION_DURATION } : SnackbarProviderProps): JSX.Element => {
  
  const [{autoHideDuration, open, message, severity}, setSnackbarContext] = useState<SnackbarContextState>({autoHideDuration: DEFAULT_AUTO_HIDE_DURATION, open: false});

  const triggerSnackbar : ShowSnackbarFunction = useCallback((message, autoHideDuration = DEFAULT_AUTO_HIDE_DURATION, severity = 'info') => {
    setSnackbarContext({
      autoHideDuration,
      message,
      severity,
      open: true,
    });
  }, []);

  const errorSnackbar : ShowSnackbarFunction = useCallback((message, autoHideDuration) => {
    triggerSnackbar(message, autoHideDuration, 'error');
  }, [triggerSnackbar]);

  const successSnackbar : ShowSnackbarFunction = useCallback((message, autoHideDuration) => {
    triggerSnackbar(message, autoHideDuration, 'success');
  }, [triggerSnackbar]);
  
  const warningSnackbar : ShowSnackbarFunction = useCallback((message, autoHideDuration) => {
    triggerSnackbar(message, autoHideDuration, 'warning');
  }, [triggerSnackbar]);
  
  const infoSnackbar : ShowSnackbarFunction = useCallback((message, autoHideDuration) => {
    triggerSnackbar(message, autoHideDuration, 'info');
  }, [triggerSnackbar]);

  const onClose = useCallback(() => setSnackbarContext(snackbarState => ({ ...snackbarState, open: false })), []);

  return (
    <Provider
      value={{
        autoHideDuration,
        transitionDuration,
        message,
        severity,
        open,
        onClose,
        errorSnackbar,
        successSnackbar,
        warningSnackbar,
        infoSnackbar,
      }}
    >
      <Snackbar
        autoHideDuration={DEFAULT_AUTO_HIDE_DURATION}
        transitionDuration={DEFAULT_TRANSITION_DURATION}
        message={message}
        severity={severity}
        open={open}
        onClose={onClose}
      />
      {children}
    </Provider>
  );
};

export const useSnackbarContext = () => useContext(SnackbarContext);

export default useSnackbarContext;